import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Facility, Organization } from '../../../utils/models';

import Conditional from '../../common/conditional';
import Header from '../../blocks/header';
import Loader from '../../themed/loader';
import NoItems from '../../blocks/no-items';
import Modal from '../../themed/modal';
import NotificationRecipients from '../../blocks/notification-recipients';
import UpdateFacility from '../../forms/updateFacility';
import UpdateOrganization from '../../forms/updateOrganization';
import Status from '../../blocks/status';

import * as facilityService from '../../../services/facilityService';
import * as service from '../../../services/organizationService';
import * as toast from '../../../services/toastService';

import classNames from './style.module.scss';
import routes from '../../../utils/routes';

const OrganizationDetails: React.FC = () => {
	const [selectedFacility, setSelectedFacility] = useState<Facility | null>();
	const [organization, setOrganization] = useState<Organization | null>();
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<boolean>(true);

	const navigate = useNavigate();
	const params = useParams();

	const getUnit = (): string => {
		const totalFacilities: number = organization?.facilities?.length ?? 0;
		return totalFacilities > 1 ? 'ies' : 'y';
	};

	const getOrganizationDetails = async (): Promise<void> => {
		const id: string | undefined = params.organizationId;
		if (!id) return;

		try {
			setLoading(true);

			const result = await service.getOrganization(id);
			setOrganization(result.data);

			setLoading(false);
			setError(false);
		} catch (error: any) {
			setError(true);
			setLoading(false);
		}
	};

	const handleFacilityDeactivate = async (facilityId: string): Promise<void> => {
		if (!organization) return;

		const allFacilities = organization.facilities;
		const newFacilities = allFacilities.filter((f) => f._id !== facilityId);

		const originalOrganization: Organization = organization;
		const newOrganization = { ...organization, facilities: newFacilities };
		setOrganization(newOrganization);

		try {
			setLoading(true);
			await facilityService.deactivateFacility(facilityId);
			setLoading(false);
		} catch (error: any) {
			toast.error(error.message);

			setLoading(false);
			setOrganization(originalOrganization);
		}
	};

	const deactivateOrganization = async (): Promise<void> => {
		try {
			setLoading(true);
			await service.deactivateOrganization(organization?._id as string);
			setLoading(false);

			toast.success('Deactivated successfully');
			navigate(routes.organizations.pathname);
		} catch (error: any) {
			setLoading(false);
		}
	};

	const activateOrganization = async (): Promise<void> => {
		try {
			setLoading(true);
			await service.activateOrganization(organization?._id as string);
			setLoading(false);

			toast.success('Activated successfully');
			navigate(routes.organizations.pathname);
		} catch (error: any) {
			setLoading(false);
		}
	};

	useEffect(() => {
		getOrganizationDetails();

		// eslint-disable-next-line
	}, [params.organizationId]);

	return (
		<React.Fragment>
			<Modal
				isVisible={!!selectedFacility}
				onClose={() => setSelectedFacility(null)}
			>
				<UpdateFacility facility={selectedFacility} />
			</Modal>

			<Header subtitle={'Showing Details for Organization: ' + (organization?.organisationName ?? 'N/A')} />

			<div className={classNames.users}>
				<Loader isVisible={loading} />

				<Conditional isVisible={!error}>
					<div className="d-flex justify-content-start align-items-center">
						<div className={classNames.userDetailsIcon}>
							<img
								src={organization?.organisationLogo}
								alt={organization?.organisationName}
							/>
						</div>

						<div className={classNames.userDetailsBody}>
							<h3>{organization?.organisationName}</h3>

							<div className={classNames.userDetailsBodySubdetails}>
								<span className={classNames.state}>
									{organization?.organisationAddress.state},{' '}
									{organization?.organisationAddress.country}
								</span>
								<span className={classNames.phone}>
									{organization?.organisationPhone} |{' '}
									{organization?.organisationEmail}
								</span>
							</div>

							<a
								href={organization?.organisationWebsite}
								target="_blank"
								rel="noopener noreferrer"
								className={classNames.userDetailsRole}
							>
								Visit Website
							</a>
						</div>

						<div className={classNames.userSummary}>
							<div className={classNames.userSummaryIcon}>
								<i className="bi bi-cone-striped"></i>
							</div>
							<div className={classNames.userSummaryDetails}>
								<div className={classNames.userSummaryDetailsValue}>
									{organization?.facilities?.length}
								</div>
								<div className={classNames.userSummaryDetailsLabel}>
									Associated Facilit{getUnit()}
								</div>
							</div>
						</div>
					</div>

					<div className="d-flex justify-content-end align-items-center mt-3">
						<Status isActive={organization?.isActive as boolean} />

						<Conditional isVisible={!organization?.isActive as boolean}>
							<button
								className={classNames.userActionButton}
								onClick={activateOrganization}
							>
								<i className="bi bi-slash-circle"></i>
								Activate
							</button>
						</Conditional>

						<Conditional isVisible={organization?.isActive as boolean}>
							<button
								className={classNames.userActionButton}
								onClick={deactivateOrganization}
							>
								<i className="bi bi-slash-circle"></i>
								Deactivate
							</button>
						</Conditional>
					</div>

					<div className="p-3 mt-5 bg-white border">
						<UpdateOrganization organization={organization} />
					</div>

					<div className="divider my-4"></div>

					<div className={classNames.usersBodyItems}>
						{organization?.facilities?.map((facility: Facility) => (
							<div className={classNames.userCard + ' mt-2'} key={facility._id}>
								<div className={classNames.userCardInitials}>
									<img src={facility.facilityLogo} alt={facility.facilityName} />
								</div>

								<div className={classNames.userCardDetails}>
									<div className={classNames.userCardDetailsName}>
										{facility.facilityName}
									</div>

									<div className={classNames.userCardDetailsEmail}>
										<span className="text-secondary">
											{facility.facilityLocation.country}
										</span>
									</div>
								</div>

								<Conditional isVisible={facility.isActive}>
									<div className="d-flex justify-content-end">
										<button
											className={classNames.userCardDetailsViewMore}
											onClick={() => handleFacilityDeactivate(facility._id)}
										>
											<i className="bi bi-trash"></i>
										</button>

										<div className="mx-3">
											<button
												className={classNames.userCardDetailsViewMore}
												onClick={() => setSelectedFacility(facility)}
											>
												<i className="bi bi-pencil text-danger"></i>
											</button>
										</div>
									</div>
								</Conditional>
								
								<Conditional isVisible={!facility.isActive}>
									<div className="d-flex justify-content-end">
										<i className="bi bi-x-octagon-fill text-danger"></i>
									</div>
								</Conditional>
							</div>
						))}
					</div>

					<div className="divider my-4"></div>

					{organization && (
						<NotificationRecipients organizationId={organization._id} />
					)}
				</Conditional>

				<NoItems isVisible={error} onRefresh={getOrganizationDetails} />
			</div>
		</React.Fragment>
	);
};

export default OrganizationDetails;
